import { gsap } from 'gsap';
import { ScrollTrigger } from 'gsap/ScrollTrigger';
import { SplitText } from 'gsap/SplitText';
import { themeColor } from '../utils/theme-color';

gsap.registerPlugin( ScrollTrigger, SplitText );

const LINE_LAG = 0.15;

// Splits a line into two stacked copies: a dimmed "base" (the pre-reveal
// look) and a full-opacity white "overlay" clipped to 0% width. The
// overlay is a sibling of base, not its descendant, so it isn't capped by
// base's own opacity — and both sit inside `wrapper`, which shrinks to the
// text's actual width so the sweep tracks the glyphs, not the full
// (text-align: center) line box. Animating `clip-path` is a hard
// geometric cut, not an interpolated blend, so the sweep has no gradient:
// each pixel is either fully "before" or fully "after".
function layerLine( line ) {
	const html = line.innerHTML;

	const wrapper = document.createElement( 'span' );
	wrapper.style.position = 'relative';
	wrapper.style.display = 'inline-block';
	wrapper.style.verticalAlign = 'top';

	const base = document.createElement( 'span' );
	base.style.display = 'block';
	base.style.opacity = '0.5';
	base.innerHTML = html;

	const overlay = document.createElement( 'span' );
	overlay.style.position = 'absolute';
	overlay.style.inset = '0';
	overlay.style.color = themeColor( '--color-white', '#fff' );
	overlay.style.clipPath = 'inset(0 100% 0 0)';
	overlay.innerHTML = html;

	wrapper.append( base, overlay );
	line.innerHTML = '';
	line.appendChild( wrapper );

	return overlay;
}

function buildReveal( sectionEl, lines ) {
	const overlays = lines.map( layerLine );

	// A single stagger across all lines' overlays: the clip-path sweep
	// (left edge of each line to its right edge) starts with the first
	// line and each following line's sweep begins after a fixed lag,
	// overlapping rather than waiting for the previous line to finish.
	return gsap.to( overlays, {
		clipPath: 'inset(0 0% 0 0)',
		ease: 'none',
		stagger: LINE_LAG,
		scrollTrigger: {
			trigger: sectionEl,
			start: 'top top',
			end: '+=100%',
			scrub: true,
			pin: true,
			// autoSplit (see initTextColorReveal below) can rebuild this
			// pinned ScrollTrigger more than once during load — observed to
			// happen twice in Firefox but only once in Chromium, for
			// reasons upstream of this code (font loading order, most
			// likely). Every ScrollTrigger.refresh() call — whatever
			// triggers it, GSAP's own included — recalculates every
			// trigger's start/end in one pass, sorted roughly by document
			// position; a trigger that was just killed and recreated (as
			// this one is, on rebuild) doesn't reliably keep its place in
			// that sort, so a *later* trigger further down the page
			// (`#caseStudyMosaic`) can get measured *before* this one's
			// pin distance is accounted for — undercounting its `start` by
			// exactly this section's own pin range (confirmed with a
			// minimal repro outside this codebase, reproducible in both
			// Firefox and Chromium, not GSAP-version-specific). A positive
			// `refreshPriority` forces this trigger to always refresh
			// first, regardless of recreation order, which is what fixes
			// it: https://gsap.com/docs/v3/Plugins/ScrollTrigger/#refreshPriority
			refreshPriority: 1,
		},
	} );
}

export function initTextColorReveal( container ) {
	if ( ! container ) {
		return;
	}

	const textEl = container.querySelector( '[data-animate="reveal-scroll"]' );

	if ( ! textEl ) {
		return;
	}

	// The opacity-50 class on the ancestor wrapper (index.php) is the
	// progressive-enhancement default for when this script hasn't run.
	// CSS opacity flattens its whole subtree's alpha before compositing,
	// so left in place it would also cap the overlay layers below at 50%
	// regardless of their own opacity — neutralize it here and
	// reproduce the same dimmed look per line instead (see layerLine),
	// where it only affects the base layer, not its overlay sibling.
	const dimmedAncestor = textEl.parentElement;

	if ( dimmedAncestor ) {
		dimmedAncestor.style.opacity = '1';
	}

	// autoSplit (below) re-splits — and rebuilds this section's pinned
	// ScrollTrigger — whenever ANY font face on the page finishes loading,
	// not just ones used by textEl. A font that's only used by content
	// that paints later (e.g. a font-weight the case-study map's info card
	// uses for the first time on click) would otherwise fire that rebuild
	// while the user is scrolled past this pinned section, which throws
	// off ScrollTrigger's scroll position by one pin range. Loading every
	// declared face now means they're all already resolved before
	// anything else on the page can be the first to request them.
	document.fonts.forEach( ( fontFace ) => fontFace.load() );

	// Wait for webfonts before the initial split: SplitText's `lines`
	// mode depends on rendered font metrics for line-wrap points, and
	// splitting against a fallback font would make ScrollTrigger pin
	// against the wrong layout on first paint.
	document.fonts.ready.then( () => {
		SplitText.create( textEl, {
			type: 'lines',
			autoSplit: true,
			onSplit( self ) {
				return buildReveal( container, self.lines );
			},
		} );
	} );
}
